import * as XLSX from "xlsx";

export const PATTERNS = {
  Zong: [
    "CALL_TYPE","MSISDN","STRT_TM","BNUMBER","MINS","SECS",
    "LAC_ID","CELL_ID","IMEI","SITE_ADDRESS","LNG","LAT",
  ],
  Jazz: [
    "CallType","Aparty","BParty","Datetime","Duration","cellid","Imsi","Imei",
    "SiteLocation","Longitude and Latitude","Node ID","IP","Port",
  ],
  Ufone: [
    "IMEI","IMSI","A Number","B Number","Start Time","End Time","Service Provider",
    "Type","Direction","Location","Cell Id","Cell Sector","Latitude","Longitude","Duration",
  ],
  Telenor: [
    "MSISDN","call_org_num","CALL_DIALED_NUM","IMSI","IMEI",
    "CALL_START_DT_TM","CALL_END_DT_TM","INBOUND_OUTBOUND_IND","Call_Network_Volume",
    "Lac_Id","Site_Id","Cell_SITE_ID","lat","longitude","CALL_TYPE","location",
  ],
};

export const PREFIX_RULES = {
  Jazz: ["030", "032"],
  Zong: ["031", "037"],
  Telenor: ["034"],
  Ufone: ["033"],
};

const EXCEL_EXTENSIONS = [".xlsx", ".xls", ".xlsm", ".xlsb"];
const CSV_EXTENSION = ".csv";
export const SUPPORTED_DATASET_EXTENSIONS = [...EXCEL_EXTENSIONS, CSV_EXTENSION];
const EXCEL_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
];
export const EXCEL_ACCEPT = [...SUPPORTED_DATASET_EXTENSIONS, ...EXCEL_MIME_TYPES, "text/csv"].join(",");
const SCI_ID_COLUMNS = [
  "MSISDN",
  "call_org_num",
  "CALL_DIALED_NUM",
  "call_dialed_num",
  "BNUMBER",
  "Aparty",
  "BParty",
  "A Number",
  "B Number",
  "IMSI",
  "Imsi",
  "IMEI",
  "Imei",
];

export function isSupportedDatasetFile(file) {
  if (!file) return false;
  const name = toStr(file.name).toLowerCase();
  const byExtension = SUPPORTED_DATASET_EXTENSIONS.some((ext) => name.endsWith(ext));
  const byMime = file.type ? EXCEL_MIME_TYPES.includes(file.type) || file.type === "text/csv" : false;
  return byExtension || byMime;
}

export function isScientificNotationString(value) {
  const s = toStr(value);
  // Require either a decimal point in the mantissa or an explicit exponent sign.
  // A bare "digits+E+digits" pattern (e.g. a hex-encoded fragment like
  // "53455454494E47") would otherwise be misdetected as scientific notation
  // and get "expanded" into a fabricated huge number.
  return /^[+-]?\d+\.\d+e[+-]?\d+$/i.test(s) || /^[+-]?\d+e[+-]\d+$/i.test(s);
}

export function expandScientificToPlain(value) {
  const input = toStr(value);
  if (!isScientificNotationString(input)) return input;

  const sign = input.startsWith("-") ? "-" : "";
  const unsigned = input.replace(/^[+-]/, "");
  const [mantissa, expRaw] = unsigned.toLowerCase().split("e");
  const exponent = parseInt(expRaw, 10);
  if (!Number.isFinite(exponent)) return input;

  const dotIndex = mantissa.indexOf(".");
  const digits = mantissa.replace(".", "");
  const decimalIndex = dotIndex === -1 ? mantissa.length : dotIndex;
  const targetIndex = decimalIndex + exponent;

  if (targetIndex <= 0) {
    const out = `0.${"0".repeat(Math.abs(targetIndex))}${digits}`;
    return `${sign}${out}`;
  }
  if (targetIndex >= digits.length) {
    const out = `${digits}${"0".repeat(targetIndex - digits.length)}`;
    return `${sign}${out}`;
  }

  const out = `${digits.slice(0, targetIndex)}.${digits.slice(targetIndex)}`;
  return `${sign}${out}`;
}

export function normalizeIdentifierValue(value) {
  if (value === null || value === undefined || value === "") return value;

  if (typeof value === "number" && Number.isFinite(value)) {
    // Telecom IDs are integer-like. Avoid scientific notation in preview/analysis.
    return Number.isInteger(value)
      ? value.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 0 })
      : value.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 20 });
  }

  const text = toStr(value);
  if (!text) return value;

  const expanded = isScientificNotationString(text) ? expandScientificToPlain(text) : text;
  if (/^-?\d+\.0+$/.test(expanded)) return expanded.replace(/\.0+$/, "");

  return expanded;
}

export function normalizeDigitsOnlyIdentifier(value) {
  const raw = toStr(value);
  if (!raw) return "";

  const normalized = toStr(normalizeIdentifierValue(raw));
  if (!normalized) return "";
  const digits = normalized.replace(/\D/g, "");
  return digits || normalized;
}

export function isLowPrecisionScientific(value) {
  const text = toStr(value);
  if (!isScientificNotationString(text)) return false;
  const [mantissa] = text.toLowerCase().split("e");
  const significantDigits = mantissa.replace(/[^0-9]/g, "").replace(/^0+/, "").length;
  return significantDigits > 0 && significantDigits <= 7;
}

export function getScientificWarningInfo(rows, headers) {
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(headers) || !headers.length) return null;
  const targetCols = headers.filter((h) => SCI_ID_COLUMNS.includes(h));
  if (!targetCols.length) return null;

  for (const col of targetCols) {
    for (let i = 0; i < rows.length && i < 400; i += 1) {
      const v = rows[i]?.[col];
      if (isLowPrecisionScientific(v)) {
        return { column: col, sample: toStr(v) };
      }
    }
  }
  return null;
}

export function getExtensionFromPath(filePath) {
  const normalized = toStr(filePath).toLowerCase().split(/[?#]/)[0];
  const lastDot = normalized.lastIndexOf(".");
  return lastDot >= 0 ? normalized.slice(lastDot) : "";
}

export function ensureSupportedExtension(extension) {
  if (SUPPORTED_DATASET_EXTENSIONS.includes(extension)) return;
  const allowed = SUPPORTED_DATASET_EXTENSIONS.join(", ");
  const shown = extension || "(none)";
  throw new Error(`Unsupported file type: ${shown}. Supported types: ${allowed}`);
}

export function parseCsvMatrix(csvText) {
  const text = toStr(csvText).replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "\"") {
      if (inQuotes && text[i + 1] === "\"") {
        cell += "\"";
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }
    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += ch;
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows;
}

export function findHeaderRowIndex(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return 0;

  let bestIndex = 0;
  let bestMatchedCount = 0;
  let bestScore = 0;

  matrix.slice(0, 200).forEach((line, index) => {
    const cells = new Set((line || []).map((value) => toStr(value).replace(/^﻿/, "")));
    Object.values(PATTERNS).forEach((expectedHeaders) => {
      const matchedCount = expectedHeaders.filter((header) => cells.has(header)).length;
      const score = matchedCount * 10 - Math.max(0, cells.size - matchedCount);
      if (matchedCount > bestMatchedCount || (matchedCount === bestMatchedCount && score > bestScore)) {
        bestIndex = index;
        bestMatchedCount = matchedCount;
        bestScore = score;
      }
    });
  });

  // Two matching known headers are enough to identify a preamble-separated table.
  return bestMatchedCount >= 2 ? bestIndex : 0;
}

export function matrixToDataset(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return { rows: [], headers: [], headerRowIndex: 0, skippedRows: 0 };
  const headerRowIndex = findHeaderRowIndex(matrix);
  const headers = (matrix[headerRowIndex] || []).map((h) => toStr(h).replace(/^﻿/, ""));
  if (!headers.length) return { rows: [], headers: [], headerRowIndex, skippedRows: headerRowIndex };

  const rows = matrix.slice(headerRowIndex + 1).filter((line) =>
    (line || []).some((value) => toStr(value) !== "")
  ).map((line) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = line?.[idx] ?? "";
    });
    return obj;
  });
  return { rows, headers, headerRowIndex, skippedRows: headerRowIndex };
}

export function parseCsvDataset(csvText) {
  return matrixToDataset(parseCsvMatrix(csvText));
}

export function parseExcelDataset(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const firstSheetName = wb.SheetNames?.[0];
  if (!firstSheetName) return { rows: [], headers: [] };
  const ws = wb.Sheets[firstSheetName];
  const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: true });
  return matrixToDataset(matrix);
}

export async function parseDatasetByExtension(extension, readText, readArrayBuffer) {
  ensureSupportedExtension(extension);
  if (extension === CSV_EXTENSION) {
    const text = await readText();
    return parseCsvDataset(text);
  }
  const buffer = await readArrayBuffer();
  return parseExcelDataset(buffer);
}

export async function loadDatasetFromFile(file) {
  if (!file) throw new Error("No file provided.");
  const extension = getExtensionFromPath(file.name);
  return parseDatasetByExtension(
    extension,
    () => file.text(),
    () => file.arrayBuffer()
  );
}

export function toStr(v) {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

/**
 * ✅ Stronger canonical MSISDN normalization (local PK format)
 * - 0092xxxxxxxxxx -> 03xxxxxxxxx
 * - 92xxxxxxxxxx   -> 03xxxxxxxxx
 * - 3xxxxxxxxx     -> 03xxxxxxxxx
 * - keeps 03xxxxxxxxx unchanged
 */
export function normalizeMSISDN(x) {
  const raw = toStr(x);
  const source = normalizeIdentifierValue(raw);
  const n = toStr(source).replace(/\D/g, "");
  if (!n) return "";

  if (n.startsWith("0092") && n.length >= 14) return "0" + n.slice(4);
  if (n.startsWith("92") && n.length >= 12) return "0" + n.slice(2);

  if (n.startsWith("0") && n.length === 11) return n;
  if (n.startsWith("3") && n.length === 10) return "0" + n;

  return n;
}

export function guessOperatorByPrefix(msisdn) {
  const p = normalizeMSISDN(msisdn);
  const prefix3 = p.slice(0, 3);
  if (PREFIX_RULES.Jazz.includes(prefix3)) return "Jazz";
  if (PREFIX_RULES.Zong.includes(prefix3)) return "Zong";
  if (PREFIX_RULES.Telenor.includes(prefix3)) return "Telenor";
  if (PREFIX_RULES.Ufone.includes(prefix3)) return "Ufone";
  return "Unknown";
}

export function computeHeaderDiff(actualHeaders, expectedHeaders) {
  const actualSet = new Set(actualHeaders);
  const expectedSet = new Set(expectedHeaders);
  const missing = expectedHeaders.filter((h) => !actualSet.has(h));
  const extra = actualHeaders.filter((h) => !expectedSet.has(h));
  return { missing, extra };
}

export function validateAgainstPattern(actualHeaders, company) {
  const expected = PATTERNS[company];
  const actualCount = actualHeaders.length;
  const expectedCount = expected.length;
  const { missing, extra } = computeHeaderDiff(actualHeaders, expected);
  const ok = missing.length === 0 && extra.length === 0 && actualCount === expectedCount;
  return { ok, company, expectedCount, actualCount, missing, extra, expected, actualHeaders };
}

export function detectCompanyByHeaders(actualHeaders) {
  const results = Object.keys(PATTERNS).map((c) => validateAgainstPattern(actualHeaders, c));
  const perfect = results.find((r) => r.ok);
  if (perfect) return { detected: perfect.company, results };
  const scored = results
    .map((r) => ({ ...r, score: r.missing.length * 3 + r.extra.length * 2 + Math.abs(r.actualCount - r.expectedCount) }))
    .sort((a, b) => a.score - b.score);
  return { detected: scored[0]?.company || "Unknown", results: scored };
}

export function parseExcelDate(value) {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === "number") {
    const d = XLSX.SSF.parse_date_code(value);
    if (!d) return null;
    return new Date(d.y, d.m - 1, d.d, d.H, d.M, d.S);
  }
  if (typeof value === "string") {
    const t = Date.parse(value.trim());
    if (!Number.isNaN(t)) return new Date(t);
  }
  return null;
}

export function parseDurationSeconds(v) {
  if (!v) return 0;
  if (typeof v === "number") return Math.max(0, Math.floor(v));
  const s = String(v).trim();
  const parts = s.split(":").map((x) => x.trim());
  if (parts.length === 3) {
    const [hh, mm, ss] = parts.map((x) => parseInt(x, 10));
    return (Number(hh) || 0) * 3600 + (Number(mm) || 0) * 60 + (Number(ss) || 0);
  }
  const n = Number(s);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/** ✅ FULL detailed mapping restored */
export function buildNormalizedRowsByCompany(rawRows, company) {
  if (company === "Zong") {
    return rawRows.map((r, idx) => ({
      __id: idx,
      aParty: normalizeMSISDN(r["MSISDN"]),
      bParty: normalizeMSISDN(r["BNUMBER"]),
      callType: toStr(r["CALL_TYPE"]),
      dateTime: parseExcelDate(r["STRT_TM"]),
      durationSec: (Number(r["MINS"]) || 0) * 60 + (Number(r["SECS"]) || 0),
      location: toStr(r["SITE_ADDRESS"]),
      lac: toStr(r["LAC_ID"]),
      cellId: toStr(r["CELL_ID"]),
      imei: normalizeDigitsOnlyIdentifier(r["IMEI"]),
      lng: toStr(r["LNG"]),
      lat: toStr(r["LAT"]),
      raw: r,
    }));
  }

  if (company === "Jazz") {
    return rawRows.map((r, idx) => ({
      __id: idx,
      aParty: normalizeMSISDN(r["Aparty"]),
      bParty: normalizeMSISDN(r["BParty"]),
      callType: toStr(r["CallType"]),
      dateTime: parseExcelDate(r["Datetime"]),
      durationSec: parseDurationSeconds(r["Duration"]),
      cellId: toStr(r["cellid"]),
      imsi: normalizeDigitsOnlyIdentifier(r["Imsi"]),
      imei: normalizeDigitsOnlyIdentifier(r["Imei"]),
      siteLocation: toStr(r["SiteLocation"]),
      longLat: toStr(r["Longitude and Latitude"]),
      nodeId: toStr(r["Node ID"]),
      ip: toStr(r["IP"]),
      port: toStr(r["Port"]),
      // for uniform analysis
      location: toStr(r["SiteLocation"]),
      raw: r,
    }));
  }

  if (company === "Ufone") {
    return rawRows.map((r, idx) => ({
      __id: idx,
      aParty: normalizeMSISDN(r["A Number"]),
      bParty: normalizeMSISDN(r["B Number"]),
      callType: `${toStr(r["Type"])} ${toStr(r["Direction"])}`.trim(),
      start: parseExcelDate(r["Start Time"]),
      end: parseExcelDate(r["End Time"]),
      dateTime: parseExcelDate(r["Start Time"]),
      durationSec: parseDurationSeconds(r["Duration"]),
      location: toStr(r["Location"]),
      cellId: toStr(r["Cell Id"]),
      cellSector: toStr(r["Cell Sector"]),
      imei: normalizeDigitsOnlyIdentifier(r["IMEI"]),
      imsi: normalizeDigitsOnlyIdentifier(r["IMSI"]),
      lat: toStr(r["Latitude"]),
      lng: toStr(r["Longitude"]),
      provider: toStr(r["Service Provider"]),
      raw: r,
    }));
  }

  if (company === "Telenor") {
    return rawRows.map((r, idx) => {
      // ✅ Telenor's "call_org_num"/"CALL_DIALED_NUM" columns hold whichever
      // party originated vs. was dialed — they do NOT consistently mean
      // "self" vs. "contact". For an INCOMING record the subscriber is the
      // dialed number and call_org_num is the actual contact; for an
      // OUTGOING record it's the reverse. Resolve the true counterparty
      // (bParty) using the direction indicator instead of hard-coding it,
      // otherwise every incoming record collapses onto the subscriber's own
      // number and the real contacts never appear in MFC.
      const ind = toStr(r["INBOUND_OUTBOUND_IND"]).toLowerCase();
      const isOutgoing = /\b(out|outgoing|outbound|mo)\b/.test(ind);
      const orgNum = r["call_org_num"];
      const dialedNum = r["CALL_DIALED_NUM"];
      const selfRaw = isOutgoing ? orgNum : dialedNum;
      const counterpartyRaw = isOutgoing ? dialedNum : orgNum;

      // ✅ GPRS/data sessions have no real "other party" — call_org_num is
      // just the subscriber's own number in these rows, so resolving a
      // counterparty for them would falsely show the subscriber as their
      // own most-frequent contact. Leave bParty blank; it'll be listed
      // under Other/Unresolved instead of polluting MFC.
      const isDataSession = /\b(gprs|data)\b/i.test(toStr(r["CALL_TYPE"]));

      return {
        __id: idx,
        aParty: normalizeMSISDN(selfRaw || r["MSISDN"]),
        bParty: isDataSession ? "" : normalizeMSISDN(counterpartyRaw),
        callType: `${toStr(r["CALL_TYPE"])} ${toStr(r["INBOUND_OUTBOUND_IND"])}`.trim(),
        start: parseExcelDate(r["CALL_START_DT_TM"]),
        end: parseExcelDate(r["CALL_END_DT_TM"]),
        dateTime: parseExcelDate(r["CALL_START_DT_TM"]),
        durationSec: parseDurationSeconds(r["Call_Network_Volume"]),
        location: toStr(r["location"]),
        lac: toStr(r["Lac_Id"]),
        siteId: toStr(r["Site_Id"]),
        cellSiteId: toStr(r["Cell_SITE_ID"]),
        imei: normalizeDigitsOnlyIdentifier(r["IMEI"]),
        imsi: normalizeDigitsOnlyIdentifier(r["IMSI"]),
        lat: toStr(r["lat"]),
        lng: toStr(r["longitude"]),
        inboundOutbound: toStr(r["INBOUND_OUTBOUND_IND"]),
        raw: r,
      };
    });
  }

  return rawRows.map((r, idx) => ({ __id: idx, raw: r }));
}

export function classifyDirectionEvent(callTypeText) {
  const t = toStr(callTypeText).toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  const tokens = t ? t.split(" ") : [];
  const hasIncomingWord = /\b(incoming|inbound)\b/.test(t);
  const hasOutgoingWord = /\b(outgoing|outbound)\b/.test(t);
  const incomingTokens = new Set(["in", "mt", "terminating", "received", "receive"]);
  const outgoingTokens = new Set(["out", "mo", "originating", "dialed", "sent", "send"]);
  const hasIncomingToken = tokens.some((x) => incomingTokens.has(x));
  const hasOutgoingToken = tokens.some((x) => outgoingTokens.has(x));
  const isIncoming = hasIncomingWord || hasIncomingToken;
  const isOutgoing = hasOutgoingWord || hasOutgoingToken;
  const isSms = t.includes("sms");
  const isData = /\b(gprs|data)\b/.test(t);
  const isCall = !isSms && !isData && (t.includes("call") || true);
  return {
    direction: isOutgoing ? "OUT" : isIncoming ? "IN" : null,
    event: isSms ? "SMS" : isData ? "DATA" : isCall ? "CALL" : "UNKNOWN",
  };
}

/**
 * ✅ Classify B-party to avoid MFC pollution + dedupe by canonical key
 */
export function classifyBParty(rawBParty) {
  const digits = toStr(rawBParty).replace(/\D/g, "");
  if (!digits) return { type: "BLANK", key: "(blank)", canonical: "" };

  // service IDs / shortcodes (3–8 digits typical)
  if (digits.length <= 8) return { type: "SHORTCODE", key: digits, canonical: digits };

  // canonicalize as PK local number if possible
  const canon = normalizeMSISDN(digits);

  // corruption/concatenation guard
  if (canon.length > 20) return { type: "INVALID", key: digits, canonical: digits };

  // proper MSISDN in local PK form
  if (canon.startsWith("0") && canon.length === 11) return { type: "MSISDN", key: canon, canonical: canon };

  return { type: "OTHER", key: canon, canonical: canon };
}

/**
 * ✅ Improved analysis:
 * - MFC uses canonical bParty (dedupe)
 * - ShortCodes separated
 * - invalid/other separated (kept for future; not shown by default)
 * - MFL/IMEI/IMEI Scan unchanged
 */
export function analyzeCDR(normRows) {
  const mfcMap = new Map();
  const shortCodeMap = new Map();
  const invalidMap = new Map();

  const mflMap = new Map();
  const imeiMap = new Map();
  const imeiScanMap = new Map();

  const bump = (map, key, event, direction) => {
    if (!map.has(key)) map.set(key, { bParty: key, inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 });
    const row = map.get(key);

    if (event === "CALL") {
      direction === "IN" ? (row.inCall += 1) : (row.outCall += 1);
      row.total += 1;
    } else if (event === "SMS") {
      direction === "IN" ? (row.inSms += 1) : (row.outSms += 1);
      row.total += 1;
    } else {
      row.total += 1;
    }
  };

  for (const r of normRows) {
    const bInfo = classifyBParty(r.bParty);
    const loc = r.location || r.siteLocation || "(blank)";
    const imei = r.imei || "(blank)";
    const a = r.aParty || "(blank)";
    const { direction, event } = classifyDirectionEvent(r.callType || "");

    // MFC buckets
    if (bInfo.type === "MSISDN") bump(mfcMap, bInfo.key, event, direction);
    else if (bInfo.type === "SHORTCODE") bump(shortCodeMap, bInfo.key, event, direction);
    else bump(invalidMap, bInfo.key, event, direction);

    // MFL
    if (!mflMap.has(loc)) mflMap.set(loc, { location: loc, inCall: 0, outCall: 0, inSms: 0, outSms: 0, total: 0 });
    const mfl = mflMap.get(loc);
    if (event === "CALL") {
      direction === "IN" ? (mfl.inCall += 1) : (mfl.outCall += 1);
      mfl.total += 1;
    } else if (event === "SMS") {
      direction === "IN" ? (mfl.inSms += 1) : (mfl.outSms += 1);
      mfl.total += 1;
    } else mfl.total += 1;

    // IMEI summary
    if (!imeiMap.has(imei)) imeiMap.set(imei, { imei, count: 0, from: null, to: null });
    const im = imeiMap.get(imei);
    im.count += 1;
    if (r.dateTime instanceof Date && !isNaN(r.dateTime.getTime())) {
      if (!im.from || r.dateTime < im.from) im.from = r.dateTime;
      if (!im.to || r.dateTime > im.to) im.to = r.dateTime;
    }

    // IMEI scan
    if (!imeiScanMap.has(imei)) imeiScanMap.set(imei, new Map());
    const sub = imeiScanMap.get(imei);
    sub.set(a, (sub.get(a) || 0) + 1);
  }

  const sortByTotal = (arr) => arr.sort((a, b) => b.total - a.total);

  const mfcAll = sortByTotal(Array.from(mfcMap.values()));
  const shortCodesAll = sortByTotal(Array.from(shortCodeMap.values()));
  const invalidAll = sortByTotal(Array.from(invalidMap.values()));

  const mflAll = Array.from(mflMap.values()).sort((a, b) => b.total - a.total);
  const imeiSummary = Array.from(imeiMap.values()).sort((a, b) => b.count - a.count);

  const imeiScan = Array.from(imeiScanMap.entries()).map(([imei, sub]) => {
    const topNumbers = Array.from(sub.entries())
      .map(([msisdn, count]) => ({ msisdn, count, guessedNetwork: guessOperatorByPrefix(msisdn) }))
      .sort((a, b) => b.count - a.count);
    return { imei, topNumbers };
  });

  return { mfcAll, shortCodesAll, invalidAll, mflAll, imeiSummary, imeiScan };
}

export function formatInsightDate(value) {
  return value instanceof Date && !Number.isNaN(value.getTime()) ? value.toLocaleString() : "-";
}

export function analyzeInsights(normRows) {
  const datedRows = normRows.filter((r) => r.dateTime instanceof Date && !Number.isNaN(r.dateTime.getTime()));
  const sorted = [...datedRows].sort((a, b) => a.dateTime - b.dateTime);
  const contactMap = new Map();
  const hourMap = new Map();
  const dayMap = new Map();
  const locationMap = new Map();
  const imeiTimeline = new Map();
  const duplicateMap = new Map();
  let durationSeconds = 0;
  let calls = 0;
  let sms = 0;

  for (const row of normRows) {
    const { direction, event } = classifyDirectionEvent(row.callType || "");
    if (event === "CALL") calls += 1;
    if (event === "SMS") sms += 1;
    durationSeconds += Number(row.durationSec) || 0;

    const contact = classifyBParty(row.bParty);
    if (contact.type === "MSISDN") {
      if (!contactMap.has(contact.key)) contactMap.set(contact.key, { contact: contact.key, events: 0, days: new Set(), directions: new Set(), durationSeconds: 0 });
      const item = contactMap.get(contact.key);
      item.events += 1;
      if (direction) item.directions.add(direction);
      item.durationSeconds += Number(row.durationSec) || 0;
      if (row.dateTime instanceof Date && !Number.isNaN(row.dateTime.getTime())) item.days.add(row.dateTime.toISOString().slice(0, 10));
    }

    if (row.dateTime instanceof Date && !Number.isNaN(row.dateTime.getTime())) {
      const hour = row.dateTime.getHours();
      const day = row.dateTime.toISOString().slice(0, 10);
      hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
      dayMap.set(day, (dayMap.get(day) || 0) + 1);
    }

    const location = row.location || row.siteLocation || "(blank)";
    locationMap.set(location, (locationMap.get(location) || 0) + 1);

    const imei = row.imei || "(blank)";
    if (!imeiTimeline.has(imei)) imeiTimeline.set(imei, []);
    if (row.dateTime instanceof Date && !Number.isNaN(row.dateTime.getTime())) imeiTimeline.get(imei).push({ dateTime: row.dateTime, aParty: row.aParty || "(blank)", location });

    const duplicateKey = [row.aParty, row.bParty, row.dateTime?.toISOString() || "", row.callType, row.imei, location].join("|");
    duplicateMap.set(duplicateKey, (duplicateMap.get(duplicateKey) || 0) + 1);
  }

  const contactNetwork = Array.from(contactMap.values()).map((item) => ({
    contact: item.contact,
    events: item.events,
    activeDays: item.days.size,
    directions: item.directions.size,
    durationMinutes: Math.round(item.durationSeconds / 60),
    score: item.events + item.days.size * 2 + item.directions.size * 3,
  })).sort((a, b) => b.score - a.score || b.events - a.events);

  const busiestHour = Array.from(hourMap.entries()).sort((a, b) => b[1] - a[1])[0];
  const busiestDay = Array.from(dayMap.entries()).sort((a, b) => b[1] - a[1])[0];
  const locations = Array.from(locationMap.entries()).map(([location, count]) => ({ location, count })).sort((a, b) => b.count - a.count);

  const bursts = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const start = sorted[i].dateTime;
    let end = i;
    while (end + 1 < sorted.length && sorted[end + 1].dateTime - start <= 60 * 60 * 1000) end += 1;
    if (end - i + 1 >= 8) bursts.push({ start, end: sorted[end].dateTime, events: end - i + 1 });
  }
  const burstWindows = bursts.filter((item, index) => index === 0 || item.start - bursts[index - 1].start > 60 * 60 * 1000).slice(0, 20);

  const imeiChanges = [];
  Array.from(imeiTimeline.entries()).forEach(([imei, events]) => {
    events.sort((a, b) => a.dateTime - b.dateTime);
    const seenParties = new Set(events.map((item) => item.aParty).filter(Boolean));
    const seenLocations = new Set(events.map((item) => item.location).filter((value) => value !== "(blank)"));
    if (seenParties.size > 1 || seenLocations.size > 1) {
      imeiChanges.push({ imei, parties: seenParties.size, locations: seenLocations.size, firstSeen: events[0]?.dateTime, lastSeen: events[events.length - 1]?.dateTime, records: events.length });
    }
  });

  const duplicateRows = Array.from(duplicateMap.values()).filter((count) => count > 1).reduce((sum, count) => sum + count - 1, 0);
  const quality = {
    totalRows: normRows.length,
    datedRows: datedRows.length,
    missingDateTime: normRows.length - datedRows.length,
    missingA: normRows.filter((r) => !r.aParty).length,
    missingB: normRows.filter((r) => !r.bParty).length,
    missingLocation: normRows.filter((r) => !(r.location || r.siteLocation)).length,
    missingImei: normRows.filter((r) => !r.imei).length,
    duplicateRows,
  };

  return {
    overview: {
      firstSeen: sorted[0]?.dateTime || null,
      lastSeen: sorted[sorted.length - 1]?.dateTime || null,
      calls,
      sms,
      durationMinutes: Math.round(durationSeconds / 60),
      activeDays: dayMap.size,
      busiestHour: busiestHour ? `${String(busiestHour[0]).padStart(2, "0")}:00 (${busiestHour[1]})` : "-",
      busiestDay: busiestDay ? `${busiestDay[0]} (${busiestDay[1]})` : "-",
    },
    contactNetwork,
    burstWindows,
    imeiChanges: imeiChanges.sort((a, b) => b.records - a.records),
    locations,
    quality,
  };
}

export function resolveSelfLabel(normRows) {
  const counts = new Map();
  for (const r of normRows || []) {
    const a = toStr(r?.aParty);
    if (!a) continue;
    counts.set(a, (counts.get(a) || 0) + 1);
  }
  let best = null;
  let bestCount = 0;
  for (const [val, c] of counts) {
    if (c > bestCount) {
      best = val;
      bestCount = c;
    }
  }
  return best || "Subscriber";
}

export function bucketByRank(index, count) {
  if (count <= 1) return "large";
  const ratio = index / (count - 1);
  if (ratio <= 1 / 3) return "large";
  if (ratio <= 2 / 3) return "medium";
  return "small";
}

export function directionColorBucket(c) {
  const total = c?.total || 0;
  if (total === 0) return "balanced";
  const inRatio = ((c.inCall || 0) + (c.inSms || 0)) / total;
  const outRatio = ((c.outCall || 0) + (c.outSms || 0)) / total;
  if (inRatio >= 0.7) return "in";
  if (outRatio >= 0.7) return "out";
  return "balanced";
}

export function parseLatLng(row) {
  let latStr = row.lat;
  let lngStr = row.lng;

  // Jazz's dedicated coordinate column ("Longitude and Latitude") is often
  // blank in real exports; the actual coordinates are embedded as a
  // "|lat|lng" suffix on siteLocation instead (confirmed against this
  // project's real Jazz sample file).
  if ((!latStr || !lngStr) && row.siteLocation) {
    const parts = toStr(row.siteLocation).split("|");
    if (parts.length >= 3) {
      latStr = parts[parts.length - 2];
      lngStr = parts[parts.length - 1];
    }
  }

  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null; // common "no GPS fix" placeholder
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}
